import styled from "@emotion/styled"
import { Overlay } from "components/articles/OverlayRoot"
import Image from "components/media/ImageCLD"
import { useConsole } from "contexts/Console"
import getMediaQuery from "css/breakpoints"
import { fullGrid } from "css/grid"
import { body50, headline30 } from "css/text"
import { motion, useScroll, useTransform } from "framer-motion"
import { useRef } from "react"
import BeautyshotButton from "./BeautyshotButton"

const Root = styled(motion.section)`
  --offset: 4px;
  --overlay-rgb: rgb(0 0 0 / 0.4);

  ${fullGrid}
  min-height: calc(200vh + var(--offset));
  position: sticky;
  top: calc(var(--offset) / -2);

  * {
    pointer-events: none;
  }

  button {
    pointer-events: auto;
  }

  > div {
    position: relative;
  }
`

const Figure = styled(motion.figure)`
  grid-column: doc;
  grid-row: 1;
  height: calc(100vh + var(--offset));
  position: sticky;
  top: calc(var(--offset) / -2);

  picture,
  img {
    display: block;
  }

  img {
    height: calc(100vh + var(--offset));
    object-fit: cover;
  }
`

const Content = styled.div`
  color: white;
  display: flex;
  flex-direction: column;
  grid-column: main;
  grid-row: 1;
  margin-block-start: 115vh;
  margin-block-end: 50vh;
  position: relative;
  row-gap: 2ex;
  text-wrap: pretty;
  z-index: 0;

  ${getMediaQuery("m")} {
    grid-column: col 3 / span 8;
  }

  ${getMediaQuery("l")} {
    grid-column: col 4 / span 6;
  }

  > div {
    display: contents;
  }

  h2 {
    ${headline30}
  }

  p {
    ${body50}

    & + p {
      margin-block-start: 0.625rem;
    }
  }

  /* a11y */
  html.prefers-contrast & div {
    color: black !important;
    position: relative;

    ::after {
      background: white;
      content: "";
      height: 100%;
      left: 50%;
      position: absolute;
      top: 50%;
      transform: translate(-50%, -50%);
      width: calc(100% + 20px);
      z-index: -1;
    }
  }
`

const P = styled.p`
  font-weight: 700;
`

const CTA = styled(BeautyshotButton)`
  align-content: end;
  display: grid;
  grid-column: doc;
  grid-row: 1;
  height: calc(100dvh + var(--offset) - 2.5rem);
  justify-content: end;
  padding-bottom: 2.5rem;
  padding-inline-end: var(--outer-margin);
  position: sticky !important;
  top: calc(var(--offset) / -2);
`

function Text({ title, chapo, paragraph }) {
  return (
    <Content>
      <div>
        {title && <h2 dangerouslySetInnerHTML={{ __html: title }} />}
        {chapo && <P dangerouslySetInnerHTML={{ __html: chapo }} />}
        {paragraph?.map(t => (
          <p key={t} dangerouslySetInnerHTML={{ __html: t }} />
        ))}
      </div>
    </Content>
  )
}

export default function ArticleOverlay({ content }) {
  const console = useConsole()
  const rroot = useRef()

  const { scrollYProgress } = useScroll({ target: rroot, offset: ["start start", "end end"] })
  const opacity = useTransform(scrollYProgress, [0, 0.6], [0, 1])

  const text = content.find(item => item.text)?.text
  const asset = content.find(item => item.asset)?.asset
  const beautyshot = content.find(item => item.beautyshot)?.beautyshot

  console.log("TestimoneeArticleOverlay(%o)", content)

  return (
    <Root ref={rroot} className='dark-theme' style={{ "--opac": opacity }}>
      {asset && (
        <Figure>
          <Image sources={asset.asset} alt={asset.alt ?? ""} sizes='100vw' loading='lazy' />
          <Overlay />
        </Figure>
      )}
      {text && <Text {...text} />}
      {beautyshot && <CTA beautyshot={beautyshot} />}
    </Root>
  )
}
